/**
 * offlineQueue data contracts — the shapes + the ConflictError signal shared
 * by the queue, its handlers, and consumers. Split from offlineQueue.ts so the
 * queue engine stays under the file-size gate (decompose-by-cohesion: this is
 * the "what's in the queue" layer, the engine is the "how it flushes" layer).
 */

import type { QueueAck } from './ackVerify'

// Re-exported so consumers of the data contracts get the ack shape from one import.
export type { QueueAck } from './ackVerify'

/**
 * WHY an entry reached a terminal (non-pending) state.
 *
 * This array is the RUNTIME SSoT, and it is what makes the queue-exit suite
 * non-vacuous: `queueExit.test.ts` iterates it and asserts that EVERY reason has
 * a user-reachable action that STRICTLY REDUCES the queue. Adding a reason here
 * without wiring an exit for it FAILS the suite — which is the whole point.
 *
 * Before this existed, the queue could mint an entry that was un-syncable
 * (deterministic 4xx pinned at maxRetries), un-purgeable (the dead-letter branch
 * `!isCritical(type)` can never be taken because EPHEMERAL_QUEUE_TYPES is empty),
 * un-retryable (retryFailedEntries reset it and re-pinned it instantly) and
 * un-deletable (no dequeue was exposed to any UI). "Never lose the data" had been
 * implemented as "never let go of it" (BUG-2026-07-14-VC559).
 */
export const QUEUE_FAILURE_REASONS = [
  /**
   * DETERMINISTIC rejection — blind replay can NEVER fix it: HTTP 400/422, a
   * ContractMismatch, OR a transient-looking outer status wrapping a deterministic
   * upstream cause (a 502 …_RPC_FAILED carrying a Postgres class-23 SQLSTATE such as
   * 23514, or a 4xx downstream code — BUG-2026-07-21-S1787). The label keeps its
   * historical name; its meaning is "deterministic, manual-retry-only".
   */
  'non-retryable-4xx',
  /** Transient failure that burned every retry. A retry MAY still succeed. */
  'retries-exhausted',
  /** Server data is newer than the local write. */
  'conflict',
  /** A NON-critical entry aged past the 72h window. */
  'expired',
  /**
   * A DEPENDENT entry whose declared predecessor (queueOrdering) is itself terminally
   * failed — e.g. the v8 visit supplement whose v2 twin never minted the engagement.
   * It was never offered to the server. Not retryable on its own: it is re-pended only
   * together with its predecessor, never ahead of it.
   */
  'predecessor-failed',
  /**
   * The door answered 401 AFTER serviceFetch's one re-handoff: the rep must log in again.
   * NOT terminal by itself — the replay layer keeps such an entry PENDING (no retry spent,
   * not captured as a failure) and the first flush after a successful login sends it; the
   * outbox (offline:writeQueue) survives the logout's clearUserCache. Listed here so the
   * queue-exit ratchet also proves a rep can get it out (retry or discard) if it is ever
   * pinned, and so a UI can say why it waits. Spelled as the work item's AC names it.
   */
  'auth_required',
] as const

export type QueueFailureReason = (typeof QUEUE_FAILURE_REASONS)[number]

export interface QueueEntry {
  id: string
  type: string           // e.g. 'appointment:create', 'visit-report:submit'
  payload: unknown
  retries: number
  maxRetries: number
  createdAt: string      // ISO
  lastAttempt?: string   // ISO
  status: 'pending' | 'failed' | 'conflict'
  /**
   * WHY this entry is terminal. OPTIONAL on purpose: entries already sitting in a
   * live rep's IndexedDB were written before this field existed, and a REQUIRED
   * field would orphan exactly the unrecovered field data this system exists to
   * protect. Absent ⇒ the entry predates reason-tagging.
   */
  failureReason?: QueueFailureReason
  /**
   * The server's verdict on an entry pinned as a FINAL refusal ('non-retryable-4xx'): the SL
   * error code and/or HTTP status as one label (retryClassification.refusalCodeOf, e.g.
   * "HTTP 403 ENGAGEMENT_NOT_OWNED"). Written ONLY where an entry is pinned final — the online
   * submit (queueStoreWrite.pinQueueEntryFinal) and the drain path (replayFailure) — so a surface can
   * tell the rep WHY it will never be re-sent. OPTIONAL for the same reason as `failureReason`:
   * entries on a rep's device predate it, and no other fate carries a server verdict.
   */
  finalCode?: string
  /**
   * ISO time before which the flush does NOT offer this pending entry again — set after a
   * retryable failure (exponential backoff with jitter, Retry-After as the floor;
   * replayFailure.retryDelayMs). OPTIONAL for the same reason as `failureReason`: entries
   * already on a rep's device predate it; absent ⇒ due now. Cleared when the entry is pinned.
   */
  nextAttemptAt?: string
}

/**
 * Handler return contract (FR-FE-IRONCLAD-TELEMETRY-OUTBOX):
 *   - `{ verified: true, serverId? }` (a {@link QueueAck}) — the server CONFIRMED
 *     the write. The entry is pruned from the durable outbox on this, and ONLY on
 *     this or a legacy void.
 *   - `void` — legacy completion signal (a handler that predates the ack contract).
 *     Trusted for backward compatibility: pruned.
 *   - a DEFINED value that is not a verified ack — a bare 2xx with no confirmation.
 *     The entry is RETAINED (never delete a rep's only copy on an unconfirmed reply).
 * Throw to retry (transient) or throw ConflictError to mark a conflict.
 */
export type QueueHandler = (payload: unknown) => Promise<QueueAck | void>

/** Throw from a handler to signal a server-side conflict (data changed since local write) */
export class ConflictError extends Error {
  public readonly serverTimestamp: string
  public readonly localTimestamp: string
  constructor(message: string, serverTs: string, localTs: string) {
    super(message)
    this.name = 'ConflictError'
    this.serverTimestamp = serverTs
    this.localTimestamp = localTs
  }
}

/**
 * Throw from a handler when the door's answer is FINAL for this body — the door's own client
 * declares which refusal codes are (e.g. visitReportV8FinalRefusalCodeOf); it is never inferred
 * from a status. replayFailure pins the entry on this first replay ('non-retryable-4xx', captured
 * off-device) instead of retrying a refusal that can never change. Carries the original
 * failure's status / code / details so the capture records the server's verdict.
 */
export class FinalRefusalError extends Error {
  public readonly status: number | undefined
  public readonly code: string
  public readonly details: unknown
  // Declared here: the ES2020 lib these sources compile against has no Error.cause.
  public readonly cause: unknown
  constructor(code: string, cause: unknown) {
    const c = cause as { message?: unknown; status?: unknown; details?: unknown } | null | undefined
    super(`${typeof c?.message === 'string' ? c.message : String(cause)} (final refusal: ${code})`)
    this.name = 'FinalRefusalError'
    this.code = code
    this.status = typeof c?.status === 'number' ? c.status : undefined
    this.details = c?.details
    this.cause = cause
  }
}

export interface ConflictEvent {
  entry: QueueEntry
  serverTimestamp: string
  localTimestamp: string
}

/**
 * WHY a flush cycle did not actually run.
 *
 * D-33 R1 — the old `flushQueue` returned a bare `{synced:0, failed:0}` when it
 * early-returned on `navigator.onLine`, which is BYTE-IDENTICAL to "I ran and
 * there was nothing to do". The queue had not drained once in eleven days and
 * every caller read that silence as success. A run that DID NOT HAPPEN must be
 * distinguishable from a run that found nothing to do.
 */
export type FlushSkipReason =
  /** Another flush is mid-cycle; this call did nothing. */
  | 'already-flushing'
  /** The reachability PROBE (never the raw flag) confirmed the server is unreachable. */
  | 'offline-confirmed'
  /** retryFailedEntries found nothing it was allowed to retry — so it did not flush. */
  | 'nothing-retryable'

/**
 * Result of a flush cycle: counts of synced + genuinely-failed entries.
 * `skipped` is present ONLY when the cycle did not run; its ABSENCE is the
 * positive assertion that the flush really executed.
 */
export interface FlushResult {
  synced: number
  failed: number
  skipped?: FlushSkipReason
}

/**
 * Result of the manual-retry path. Extends FlushResult so existing consumers keep
 * compiling, and adds the two counts that make a no-op HONEST: `retried` is how
 * many entries were actually reset to pending; `notRetryable` is how many were
 * left alone because replay can never fix them. `retried === 0 && notRetryable > 0`
 * is the "you pressed retry and nothing could possibly change" case, which the UI
 * must be able to tell the rep instead of spinning and re-pinning forever.
 */
export interface RetryFailedResult extends FlushResult {
  retried: number
  notRetryable: number
}

/** A queue entry projected for a UI surface — identity + fate, never the raw payload. */
export interface QueueEntryView {
  id: string
  type: string
  createdAt: string
  lastAttempt?: string
  retries: number
  maxRetries: number
  status: QueueEntry['status']
  failureReason?: QueueFailureReason
  /** The server's verdict on a FINAL pin (QueueEntry.finalCode) — absent exactly where the entry has none. */
  finalCode?: string
  /** True when a retry could plausibly change the outcome (drives the Retry control). */
  retryable: boolean
}

/**
 * Outcome of a user-initiated Discard.
 *
 * `discarded: false` is NOT a soft failure to swallow — it means the durable
 * off-device capture did NOT confirm, so the entry was DELIBERATELY LEFT IN THE
 * QUEUE. Deleting a rep's only copy of a visit report because a capture call
 * "probably worked" is the exact class of loss this system exists to prevent, so
 * the sink's REAL outcome is asserted before anything is removed.
 */
export interface DiscardOutcome {
  discarded: boolean
  /** Present when `discarded` is false — why the entry was KEPT. */
  reason?: 'capture-not-confirmed' | 'entry-not-found'
  /** The error the durable sink actually threw, when it threw. */
  captureError?: string
}
